globalThis["__sentryRewritesTunnelPath__"] = undefined;
globalThis["SENTRY_RELEASE"] = {"id":"46a004bb800490222808a00c9c13e2ecb4e4c8b1"};
globalThis["__sentryBasePath"] = undefined;
globalThis["__rewriteFramesAssetPrefixPath__"] = "";
// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import * as Sentry from "@sentry/nextjs";

Sentry.init({
    dsn: "https://a789800d71a7440e66bbeeff36cfaedd@o4506659063857152.ingest.us.sentry.io/4508976098508800",

    // Sample 2% of transactions. At 1 (100%) every page view and request on
    // these high-traffic news sites became a Sentry transaction; 2% is still a
    // large sample for performance work. Errors are not sampled by this setting.
    tracesSampleRate: 0.02,

    // Setting this option to true will print useful information to the console while you're setting up Sentry.
    debug: false,

    // Noise that is not an application bug. Every pattern is anchored, because
    // Sentry matches a plain string by substring and an unanchored fragment
    // could swallow a real first-party error (e.g. "Failed to fetch user
    // profile" being hidden by a bare "Failed to fetch").
    ignoreErrors: [
        // Browser/network: a fetch aborted by navigation or a dropped
        // connection, including the App Router's own route prefetches.
        // PLATFORM-ALAKHBAR-15.
        /^Load failed$/,
        /^Failed to fetch$/,
        /^NetworkError when attempting to fetch resource\.?$/,
        /^AbortError: /,
        /^The (user aborted a request|operation was aborted)\.?$/,

        // Third-party/native code we neither ship nor can fix from this repo:
        // the Google Ad Manager anchor/companion script touching its own
        // `CONFIG` global before it is ready, a tag inside the GTM container
        // assuming a global `jQuery` this site has never loaded, an analytics
        // beacon calling `Array.prototype.findLast` on browsers without it,
        // and the in-app Android/Facebook WebView bridge failing to deliver a
        // postMessage to the native shell (usually during teardown). None have
        // first-party frames. `M_ID` (PLATFORM-ALAKHBAR-1Y) joins this group:
        // thrown from a minified ad executor bundle (`executors/*.js`, no
        // first-party frames, mechanism onunhandledrejection) reading a
        // creative field off its own not-yet-ready config object.
        /Cannot read properties of undefined \(reading 'M_ID'\)/,
        //
        // The jQuery pattern (PLATFORM-ALAKHBAR-2N) cannot swallow one of ours:
        // jQuery is not a dependency, is not loaded by any script tag, and
        // appears nowhere in `apps/*` or `packages/*`. The stack sits entirely
        // inside the container's own tag execution, so the fix belongs in the
        // GTM container config, not here — this only stops it being reported
        // as an application bug. Both spellings are listed because WebKit and
        // V8 word a missing global differently.
        /Can't find variable: CONFIG/,
        /CONFIG is not defined/,
        /Can't find variable: jQuery/,
        /jQuery is not defined/,
        /findLast is not a function/,
        /Error invoking postMessage/,
    ],

    beforeSend(event, hint) {
        const originalException = hint?.originalException;

        // Some in-app browsers (Twitter/Facebook WebViews) dispatch
        // resource-load `error` events — a stylesheet failing to load, say —
        // in a way that gets wrapped and reported as an unhandled promise
        // rejection even though nothing rejected. These carry an Event rather
        // than an Error and are not actionable application bugs.
        if (
            typeof Event !== "undefined" &&
            originalException instanceof Event &&
            event.exception?.values?.some(
                (value) =>
                    value.mechanism?.handled === false &&
                    value.mechanism?.type === "onunhandledrejection"
            )
        ) {
            return null;
        }

        // PLATFORM-ALAKHBAR-6Y: a browser extension's own injected content
        // script (`injectScript.js` — not a file in this repo) tries to
        // JSON.stringify a DOM node carrying a React Fiber back to itself
        // (`propagateToExtension` / a `"GTMToExtension"` custom event) and
        // hits the circular reference React always creates. The message text
        // includes whichever DOM element the extension happened to touch, so
        // it can't be matched with a fixed `ignoreErrors` string; the stable
        // signal is the frame naming the extension's own script.
        if (
            event.exception?.values?.some((value) =>
                value.stacktrace?.frames?.some((frame) =>
                    frame.filename?.includes("injectScript.js")
                )
            )
        ) {
            return null;
        }

        return event;
    },
});
